import React, { useState } from "react";
import { Card, Nav, Pagination, Table } from "react-bootstrap";
import Select from "react-select";
import { showPageNumbers } from "../../utils/table";
import { TableSkeleton } from "../../skeleton/TableSkeleton";
import styles from "./index.module.css";
import clsx from "clsx";
import { FaHistory, FaRegEye } from "react-icons/fa";
import { GlobalModal } from "../../helper/GlobalModal";
import TrancheOpportunityCard from "./TrancheOpportunityCard";
import { tracker } from "../../service/mixpanel";

const paginationOptions = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
];

const DetailedLogsTable = ({
  isLoading,
  rows,
  currentPage,
  setCurrentPage,
  totalDocs,
  itemsPerPage,
  setItemsPerPage,
  setFilterDate,
  setSearchText,
  searchText,
  filterDate,
}) => {
  const totalPages = Math.ceil(totalDocs / itemsPerPage);
  const showPaginationNumbers = totalPages > 10;
  const [showModal, setShowModal] = useState(false);
  const [trancheId, setTrancheId] = useState(null);
  const handleTrancheIdClick = (trancheId) => {
    window.open(
      trancheId,
      "_blank"
    );
  };

  const handleViewClick = (id) => {
    tracker.trancheBotV2.trancheClick(id);
    setTrancheId(id);
    setShowModal(true);
  };

  return (
    <div className={styles.detailsLogsContainer}>
      <div className={styles.detailsLogsHeaders}>
        <h1 className={styles.analysisSubHeader}>Detailed Log</h1>
        <div className={styles.detailsLogsHeaders}>
          <div className={styles.detailsLogsInputFields}>
            <p>Filter by:</p>
            <input
              type="text"
              placeholder="Search...."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onBlur={() => setCurrentPage(1)} // fetch when leaving input
            />
          </div>
          <div className={styles.detailsLogsInputFields}>
            <p>Date:</p>
            <input
              type="date"
              value={filterDate}
              onChange={(e) => {
                setFilterDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton />
      ) : (
        <div className="scrollable-table-container2">
          <Card
            border="light"
            className="table-wrapper table-responsive shadow-sm"
          >
            <Card.Body className="pt-0">
              <Table hover className="user-table align-items-center">
                <thead>
                  <tr>
                    <th className={styles.detailLogHeaders}>TimeStamp</th>
                    <th className={styles.detailLogHeaders}>Tranche ID</th>
                    <th className={styles.detailLogHeaders}>Opportunity ID</th>
                    <th className={styles.detailLogHeaders}>User</th>
                    <th className={styles.detailLogHeaders}>Equity</th>
                    <th className={styles.detailLogHeaders}>ITC%</th>
                    <th
                      className={clsx(
                        styles.detailLogHeaders,
                        styles.statusClass
                      )}
                    >
                      Status
                    </th>
                    <th className={styles.detailLogHeaders}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {!rows.length ? (
                    <tr>
                      <td colSpan={7} className="bg-grey">
                        No Data Found.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row) => {
                      const status = row.status?.toLowerCase();
                      return (
                        <tr key={row._id}>
                          <td className={styles.detailedLogsValue}>
                            {row.timestamp}
                          </td>
                          <td
                            className={styles.detailTableTrancheId}
                            onClick={() => {
                              if (
                                row?.status === "Success" &&
                                row?.zohoTrancheId
                              ) {
                                handleTrancheIdClick(row?.zohoTrancheId);
                              }
                            }}
                          >
                            {row?.zohoTrancheId
                              ? row.zohoTrancheId.split("/").pop()
                              : "-"}
                          </td>
                          <td className={styles.detailedLogsValue}>
                            {row.opportunityId}
                          </td>
                          <td className={styles.detailedLogsValue}>
                            {row.user}
                          </td>
                          <td className={styles.detailedLogsValue}>
                            {row.equity}
                          </td>
                          <td className={styles.detailedLogsValue}>
                            {row.itc}
                          </td>
                          <td className={styles.detailedLogsValue}>
                            <span
                              className={clsx(styles.statusCapsule, {
                                [styles.statusSuccess]: status === "success",
                                [styles.statusRed]: status === "failed",
                                [styles.statusYellow]: status === "pending",
                                [styles.statusOrange]:
                                  status?.includes("draft"),
                                [styles.statusBlue]:
                                  status === "sent for approval",
                                [styles.statusFailure]:
                                  ![
                                    "success",
                                    "pending",
                                    "sent for approval",
                                  ].includes(status) &&
                                  !status?.includes("draft"),
                              })}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td className={styles.detailedLogsValue}>
                            <FaHistory
                              size={20}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleViewClick(row._id)}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {/* Pagination + Items Per Page */}
          <Card.Footer
            className={clsx(
              "px-3 border-0 d-lg-flex justify-content-between bg-white",
              styles.stickyFooter
            )}
          >
            <Nav>
              <Pagination className="mb-2 mb-lg-0">
                <Pagination.Prev
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </Pagination.Prev>

                {/* ✅ Render numbered pagination if total pages > 1 */}
                {totalPages > 1 && (
                  <>
                    {/* Always show page 1 */}
                    <Pagination.Item
                      active={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    >
                      1
                    </Pagination.Item>

                    {/* Show ellipsis if currentPage is beyond 3 */}
                    {currentPage > 3 && <Pagination.Ellipsis disabled />}

                    {/* Show nearby pages around current page */}
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (page) =>
                          page !== 1 &&
                          page !== totalPages &&
                          page >= currentPage - 1 &&
                          page <= currentPage + 1
                      )
                      .map((page) => (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </Pagination.Item>
                      ))}

                    {/* Show ellipsis if not near the end */}
                    {currentPage < totalPages - 2 && (
                      <Pagination.Ellipsis disabled />
                    )}

                    {/* Always show last page if totalPages > 1 */}
                    {totalPages > 1 && (
                      <Pagination.Item
                        active={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        {totalPages}
                      </Pagination.Item>
                    )}
                  </>
                )}

                <Pagination.Next
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Next
                </Pagination.Next>
              </Pagination>
            </Nav>

            {/* Items per page dropdown */}
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              Items Per Page
              <Select
                options={paginationOptions}
                defaultValue={paginationOptions.find(
                  (opt) => opt.value === itemsPerPage
                )}
                onChange={(selected) => {
                  if (selected) {
                    setItemsPerPage(selected.value);
                    setCurrentPage(1); // Reset to first page
                  }
                }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: "32px",
                    height: "32px",
                    fontSize: "14px",
                  }),
                  valueContainer: (provided) => ({
                    ...provided,
                    height: "32px",
                    padding: "0 6px",
                  }),
                  indicatorsContainer: (provided) => ({
                    ...provided,
                    height: "32px",
                  }),
                  option: (provided) => ({
                    ...provided,
                    fontSize: "14px",
                    padding: "6px 12px",
                    minHeight: "28px",
                  }),
                  menuList: (provided) => ({
                    ...provided,
                    paddingTop: 0,
                    paddingBottom: 0,
                  }),
                }}
                menuPlacement="top"
              />
            </div>
          </Card.Footer>
        </div>
      )}
      <GlobalModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Tranche Details"
        size="lg"
      >
        <TrancheOpportunityCard trancheId={trancheId} />
      </GlobalModal>
    </div>
  );
};

export default DetailedLogsTable;
